import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import DatePicker from '@atlaskit/datetime-picker/date-picker';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { DateRangeError, getInvalidDateRange } from '../../../../utils/filters';
import { i18n } from '../messages';

export const customErrors: {
	fromAfterToError: React.JSX.Element;
	fromInFutureError: React.JSX.Element;
	toInFutureError: React.JSX.Element;
} = {
	[DateRangeError.FROM_AFTER_TO_ERROR]: (
		<FormattedMessage {...i18n.dateFilterCustomInvalidDateAfterEnd} />
	),
	[DateRangeError.FROM_IN_FUTURE_ERROR]: (
		<FormattedMessage {...i18n.dateFilterCustomInvalidDateAfterToday} />
	),
	[DateRangeError.TO_IN_FUTURE_ERROR]: (
		<FormattedMessage {...i18n.dateFilterCustomInvalidToDateAfterToday} />
	),
};

const datePickerWrapper = xcss({
	width: '155px',
});

type DateRangePickerProps = {
	onCustomDateRangeChange: (from: string, to: string) => void;
	customDateRange: Record<'from' | 'to', string>;
	testId?: string;
};

const DateRangePicker = ({
	onCustomDateRangeChange,
	customDateRange,
	testId,
}: DateRangePickerProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();

	const [selectedFromDate, setSelectedFromDate] = useState<string>(customDateRange.from);
	const [selectedToDate, setSelectedToDate] = useState<string>(customDateRange.to);

	const invalidDateRange = getInvalidDateRange(selectedFromDate, selectedToDate);

	const handleUpdateSelectedDates = () => {
		if (Boolean(invalidDateRange)) {
			return;
		}
		onCustomDateRangeChange(selectedFromDate, selectedToDate);
	};

	return (
		<Box paddingInline="space.150" paddingBlock="space.075" testId={testId}>
			<Stack space="space.150">
				<Inline alignInline="start" alignBlock="center" spread="space-between">
					<Box xcss={datePickerWrapper}>
						<DatePicker
							onChange={(date) => setSelectedFromDate(date)}
							defaultValue={selectedFromDate}
							placeholder={formatMessage(i18n.dateFilterFromTitle)}
							isInvalid={Boolean(invalidDateRange)}
							locale={locale}
							label={formatMessage(i18n.dateFilterFromLabel)}
							shouldShowCalendarButton
						/>
					</Box>
					<div>{formatMessage(i18n.dateFilterToRange)}</div>
					<Box xcss={datePickerWrapper}>
						<DatePicker
							onChange={(date) => setSelectedToDate(date)}
							defaultValue={selectedToDate}
							placeholder={formatMessage(i18n.dateFilterToTitle)}
							isInvalid={invalidDateRange === DateRangeError.FROM_AFTER_TO_ERROR}
							locale={locale}
							label={formatMessage(i18n.dateFilterToLabel)}
							shouldShowCalendarButton
						/>
					</Box>
				</Inline>
				{invalidDateRange ? (
					<Box paddingBlock="space.050">
						<Inline alignBlock="center" space="space.050">
							<StatusErrorIcon color={token('color.icon.danger')} label="Date range error" />
							<Text color="color.text.danger">{customErrors[invalidDateRange]}</Text>
						</Inline>
					</Box>
				) : null}
				<Box>
					<Button isDisabled={Boolean(invalidDateRange)} onClick={handleUpdateSelectedDates}>
						{formatMessage(i18n.dateFilterUpdateButton)}
					</Button>
				</Box>
			</Stack>
		</Box>
	);
};

export default DateRangePicker;
